import { CancelWorkOrderDialog } from '@/components/features/WorkOrders/modals/CancelWorkOrderDialog';
import { CompleteWorkOrderDialog } from '@/components/features/WorkOrders/modals/CompleteWorkOrderDialog';
import { AddressMapCard } from '@/components/global/address/AddressMapCard';
import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { CompanySelect } from '@/components/global/CompanySelect';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDate } from '@/lib/utils';
import { type Company, type User, type WorkOrder } from '@/types';
import { Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface EditWorkOrderDialogProps {
    isOpen: boolean;
    onClose: () => void;
    workOrder: WorkOrder;
    users?: User[];
    companies?: Company[];
    onSave?: (data: Partial<WorkOrder>) => void;
    canEditCompanies?: boolean;
    canEditDescription?: boolean;
    statuses?: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    types?: Array<{ value: string; label: string; color: string; icon?: string }>;
    canEditStatusPriorityType?: boolean;
}

export function EditWorkOrderDialog({
    isOpen,
    onClose,
    workOrder,
    companies = [],
    onSave,
    canEditCompanies = true,
    canEditDescription = true,
    statuses = [],
    priorities = [],
    types = [],
    canEditStatusPriorityType = false,
}: EditWorkOrderDialogProps) {
    const { t } = useTranslations();
    const normalizeSettingValue = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '_');
    const translatedStatuses = useMemo(
        () =>
            statuses.map((status) => ({
                ...status,
                label: t(`settings.work_order_statuses.${normalizeSettingValue(status.value)}`, status.label),
            })),
        [statuses, t],
    );
    const translatedPriorities = useMemo(
        () =>
            priorities.map((priority) => ({
                ...priority,
                label: t(`settings.work_order_priorities.${normalizeSettingValue(priority.value)}`, priority.label),
            })),
        [priorities, t],
    );
    const translatedTypes = useMemo(
        () =>
            types.map((type) => ({
                ...type,
                label: t(`settings.work_order_types.${normalizeSettingValue(type.value)}`, type.label),
            })),
        [types, t],
    );
    const [formData, setFormData] = useState({
        description: workOrder.description,
        status: workOrder.status,
        priority: workOrder.priority || 'medium',
        work_type: workOrder.work_type || '',
        service_address:
            workOrder.service_address ||
            (workOrder.service_address_street
                ? [workOrder.service_address_street, workOrder.service_address_house_number].filter(Boolean).join(' ')
                : ''),
        service_address_lat: workOrder.service_address_lat,
        service_address_lng: workOrder.service_address_lng,
        service_address_street: workOrder.service_address_street,
        service_address_house_number: workOrder.service_address_house_number,
        service_address_city: workOrder.service_address_city,
        service_address_postal_code: workOrder.service_address_postal_code,
        service_address_place_id: workOrder.service_address_place_id,
        company_id: workOrder.company_id || '',
        paying_company_id: workOrder.paying_company?.id || '',
        estimated_date: workOrder.estimated_date || '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusSelection, setStatusSelection] = useState(workOrder.status);
    const [prioritySelection, setPrioritySelection] = useState(workOrder.priority || 'medium');
    const [typeSelection, setTypeSelection] = useState(workOrder.work_type || '');
    const [completionData, setCompletionData] = useState<{ hours: number; minutes: number; notes?: string } | null>(null);
    const [cancellationReason, setCancellationReason] = useState<string | null>(null);
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(workOrder.estimated_date ? new Date(workOrder.estimated_date) : undefined);

    useEffect(() => {
        if (isOpen) {
            setFormData({
                description: workOrder.description,
                status: workOrder.status,
                priority: workOrder.priority || 'medium',
                work_type: workOrder.work_type || '',
                service_address:
                    workOrder.service_address ||
                    (workOrder.service_address_street
                        ? [workOrder.service_address_street, workOrder.service_address_house_number].filter(Boolean).join(' ')
                        : ''),
                service_address_lat: workOrder.service_address_lat,
                service_address_lng: workOrder.service_address_lng,
                service_address_street: workOrder.service_address_street,
                service_address_house_number: workOrder.service_address_house_number,
                service_address_city: workOrder.service_address_city,
                service_address_postal_code: workOrder.service_address_postal_code,
                service_address_place_id: workOrder.service_address_place_id,
                company_id: workOrder.company_id || '',
                paying_company_id: workOrder.paying_company?.id || '',
                estimated_date: workOrder.estimated_date || '',
            });
            setStatusSelection(workOrder.status);
            setPrioritySelection(workOrder.priority || 'medium');
            setTypeSelection(workOrder.work_type || '');
            setCompletionData(null);
            setCancellationReason(null);
            setShowCompleteDialog(false);
            setShowCancelDialog(false);
            setSelectedDate(workOrder.estimated_date ? new Date(workOrder.estimated_date) : undefined);
        }
    }, [isOpen, workOrder]);

    const handleStatusSelect = (value: string) => {
        if (value === 'completed') {
            setShowCompleteDialog(true);
            return;
        }
        if (value === 'cancelled') {
            setShowCancelDialog(true);
            return;
        }

        setCompletionData(null);
        setCancellationReason(null);
        setStatusSelection(value);
        setFormData((prev) => ({ ...prev, status: value }));
    };

    const handlePrioritySelect = (value: string) => {
        setPrioritySelection(value);
        setFormData((prev) => ({ ...prev, priority: value }));
    };

    const handleTypeSelect = (value: string) => {
        setTypeSelection(value);
        setFormData((prev) => ({ ...prev, work_type: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            const completionExtras = completionData
                ? {
                      completed_at: new Date().toISOString(),
                      time_spent: `${completionData.hours}h ${completionData.minutes}m`,
                      completion_notes: completionData.notes,
                  }
                : {};
            const cancellationExtras = cancellationReason ? { cancellation_reason: cancellationReason } : {};

            onSave?.({
                ...formData,
                status: statusSelection,
                priority: prioritySelection,
                work_type: typeSelection,
                ...completionExtras,
                ...cancellationExtras,
            });
            setIsSubmitting(false);
            onClose();
        }, 500);
    };

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('work_orders.edit_work_order.title', 'Edit Work Order')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('work_orders.edit_work_order.description', 'Update the work order details. Changes will be saved immediately.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="description">{t('work_orders.edit_work_order.labels.description', 'Description *')}</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                required
                                rows={4}
                                disabled={isSubmitting || !canEditDescription}
                                readOnly={!canEditDescription}
                            />
                        </div>

                        {canEditStatusPriorityType && (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div className="space-y-2">
                                    <Label htmlFor="status">{t('work_orders.edit_work_order.labels.status', 'Status')}</Label>
                                    <Select value={statusSelection} onValueChange={handleStatusSelect} disabled={isSubmitting}>
                                        <SelectTrigger id="status" className="w-full">
                                            <SelectValue placeholder={t('work_orders.date_status.select_status', 'Select status')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedStatuses.map((status) => (
                                                <SelectItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="priority">{t('work_orders.edit_work_order.labels.priority', 'Priority')}</Label>
                                    <Select value={prioritySelection} onValueChange={handlePrioritySelect} disabled={isSubmitting}>
                                        <SelectTrigger id="priority" className="w-full">
                                            <SelectValue
                                                placeholder={t('work_orders.forms.create.placeholders.select_priority', 'Select priority...')}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedPriorities.map((priority) => (
                                                <SelectItem key={priority.value} value={priority.value}>
                                                    {priority.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="work_type">{t('work_orders.edit_work_order.labels.work_type', 'Type')}</Label>
                                    <Select
                                        value={typeSelection as string}
                                        onValueChange={(value: string) => handleTypeSelect(value)}
                                        disabled={isSubmitting}
                                    >
                                        <SelectTrigger id="work_type" className="w-full">
                                            <SelectValue
                                                placeholder={t('work_orders.forms.create.placeholders.select_work_type', 'Select work type...')}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedTypes.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="company_id">{t('work_orders.edit_work_order.labels.service_company', 'Service Company')}</Label>
                                <CompanySelect
                                    value={formData.company_id}
                                    onValueChange={(value) => setFormData((prev) => ({ ...prev, company_id: value }))}
                                    placeholder={t('work_orders.edit_work_order.placeholders.select_company', 'Select a company')}
                                    seedCompanies={companies}
                                    disabled={!canEditCompanies || isSubmitting}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="paying_company_id">
                                    {t('work_orders.edit_work_order.labels.billing_company', 'Billing Company')}
                                </Label>
                                <CompanySelect
                                    value={formData.paying_company_id}
                                    onValueChange={(value) => setFormData((prev) => ({ ...prev, paying_company_id: value }))}
                                    placeholder={t('work_orders.edit_work_order.placeholders.select_billing_company', 'Select billing company')}
                                    seedCompanies={companies}
                                    disabled={!canEditCompanies || isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="estimated_date">{t('work_orders.edit_work_order.labels.estimated_date', 'Estimated Date')}</Label>
                            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className={cn('w-full justify-start text-left font-normal', !selectedDate && 'text-muted-foreground')}
                                        disabled={isSubmitting}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {selectedDate ? (
                                            formatDate(selectedDate)
                                        ) : (
                                            <span>{t('work_orders.edit_work_order.placeholders.pick_a_date', 'Pick a date')}</span>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={(date) => {
                                            setSelectedDate(date);
                                            if (date) {
                                                setFormData((prev) => ({ ...prev, estimated_date: formatDate(date) }));
                                            } else {
                                                setFormData((prev) => ({ ...prev, estimated_date: '' }));
                                            }
                                            setIsDatePickerOpen(false);
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="service_address">{t('work_orders.edit_work_order.labels.service_address', 'Service Address *')}</Label>
                            <GoogleMapsProvider>
                                <AddressSearchInput
                                    value={formData.service_address}
                                    onChange={(value) => {
                                        setFormData((prev) => ({ ...prev, service_address: value }));
                                    }}
                                    onAddressSelect={(address) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            service_address: address.label,
                                            service_address_lat: address.lat,
                                            service_address_lng: address.lng,
                                            service_address_street: address.street,
                                            service_address_house_number: address.house_number,
                                            service_address_city: address.city,
                                            service_address_postal_code: address.postal_code,
                                            service_address_place_id: address.id,
                                        }));
                                    }}
                                    placeholder={t('work_orders.edit_work_order.placeholders.enter_address', 'Enter or search for address...')}
                                />
                                <div className="bg-muted/40 mt-3 rounded-lg border p-2">
                                    <AddressMapCard
                                        address={formData.service_address}
                                        lat={formData.service_address_lat}
                                        lng={formData.service_address_lng}
                                    />
                                </div>
                            </GoogleMapsProvider>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            {t('work_orders.edit_work_order.buttons.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('work_orders.edit_work_order.buttons.saving', 'Saving...')}
                                </>
                            ) : (
                                t('work_orders.edit_work_order.buttons.save_changes', 'Save Changes')
                            )}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>

            <CompleteWorkOrderDialog
                workOrder={workOrder}
                isOpen={showCompleteDialog}
                onClose={() => {
                    setShowCompleteDialog(false);
                    setStatusSelection(formData.status);
                }}
                onComplete={(data) => {
                    setCompletionData(data);
                    setCancellationReason(null);
                    setStatusSelection('completed');
                    setFormData((prev) => ({ ...prev, status: 'completed' }));
                    setShowCompleteDialog(false);
                }}
            />

            <CancelWorkOrderDialog
                workOrder={workOrder}
                isOpen={showCancelDialog}
                onClose={() => {
                    setShowCancelDialog(false);
                    setStatusSelection(formData.status);
                }}
                onCancel={(reason) => {
                    setCancellationReason(reason);
                    setCompletionData(null);
                    setStatusSelection('cancelled');
                    setFormData((prev) => ({ ...prev, status: 'cancelled' }));
                    setShowCancelDialog(false);
                }}
            />
        </ResponsiveModal>
    );
}
